import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ArrowLeft, Wallet, TrendingUp, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const GestionBankroll = () => {
  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      <main className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link
            to="/guides"
            data-discover="true"
            className="inline-flex items-center text-toxic-green hover:text-opacity-80 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux guides
          </Link>

          <article className="bg-dark-card border-2 border-gray-800 rounded-2xl p-8 md:p-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Gestion de Bankroll
            </h1>

            <div className="prose prose-invert max-w-none">
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                La gestion de bankroll est l'aspect le plus sous-estimé des paris en ligne, et pourtant c'est celui qui fait la différence entre les parieurs qui perdent tout et ceux qui réussissent sur le long terme. Peu importe votre niveau de connaissance sportive ou votre capacité à identifier les bonnes opportunités, sans une gestion rigoureuse de votre budget, vous finirez par perdre. Ce guide vous apprend à protéger et faire croître votre bankroll.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Définir Votre Bankroll Initial
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Votre bankroll est le montant total que vous êtes prêt à investir dans vos paris. Ce n'est pas l'argent dont vous avez besoin pour vivre, mais un budget dédié que vous pouvez vous permettre de perdre sans impact sur votre vie quotidienne. Si perdre cet argent vous causerait des problèmes financiers, vous n'avez pas de bankroll - vous avez un problème.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Fixez un montant réaliste basé sur vos revenus et vos obligations. Pour un débutant, commencer avec 200€ à 500€ est raisonnable. Cela vous permet d'apprendre sans risquer trop, tout en ayant assez pour faire des paris significatifs. Ne jamais dépasser ce montant initial, même après des gains. Votre bankroll doit être sacré.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Une fois votre bankroll définie, séparez-la mentalement (et physiquement si possible) de vos autres finances. Ne touchez jamais à votre épargne, à votre loyer, ou à tout autre argent essentiel. Les paris doivent rester un loisir, pas une nécessité financière.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                La Règle des Unités
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                La méthode la plus efficace pour gérer votre bankroll est d'utiliser des unités. Une unité représente un pourcentage fixe de votre bankroll total, généralement entre 1% et 5%. Si votre bankroll est de 1000€ et que vous utilisez des unités de 2%, chaque unité vaut 20€. Vos mises varient donc selon la taille de votre bankroll, pas selon vos émotions.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Quand votre bankroll augmente après des gains, vos unités augmentent aussi. Si vous passez de 1000€ à 1500€, vos unités de 2% passent de 20€ à 30€. Inversement, après des pertes, vos unités diminuent. Cela protège votre bankroll en période difficile et la fait croître en période favorable.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Ne jamais miser plus d'une unité sur un seul pari, sauf dans des circonstances exceptionnelles et bien réfléchies. Même si vous êtes très confiant, respecter la limite d'une unité vous protège contre les mauvaises surprises. Les favoris perdent, les certitudes échouent, et votre bankroll doit survivre à ces moments.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Ajuster Selon le Niveau de Confiance
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Vous pouvez ajuster légèrement vos mises selon votre niveau de confiance, mais toujours dans des limites strictes. Un pari très confiant peut représenter 1.5 unités, un pari moyen 1 unité, un pari risqué 0.5 unité. Mais ne jamais dépasser 2 unités, même pour ce qui semble être une "certitude absolue".
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Cette variation doit être basée sur une analyse objective, pas sur une intuition ou une émotion. Si vous ne pouvez pas expliquer clairement pourquoi vous êtes plus confiant sur ce pari que sur les autres, restez à 1 unité. L'émotion est l'ennemi de la gestion de bankroll.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Gérer les Périodes Difficiles
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Les séries de pertes sont inévitables. Même les meilleurs parieurs connaissent des périodes difficiles. La clé est de ne pas paniquer et de ne pas augmenter vos mises pour "rattraper" les pertes. C'est exactement le contraire qu'il faut faire : réduisez légèrement vos unités pendant une mauvaise passe.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Si vous perdez 20% de votre bankroll, passez temporairement de 2% à 1.5% par unité. Cela vous donne plus de marge pour rebondir sans risquer de tout perdre. Une fois que vous avez retrouvé votre niveau initial, vous pouvez revenir à vos unités normales.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Fixez-vous une limite de perte. Si vous perdez 50% de votre bankroll initial, arrêtez-vous. Prenez du recul, analysez ce qui n'a pas fonctionné, et ne reprenez qu'avec un nouveau bankroll si vous décidez de continuer. Perdre 50% est récupérable, perdre 90% ne l'est généralement pas.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Faire Croître Votre Bankroll
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Quand vous gagnez, ne retirez pas tout immédiatement. Laissez une partie de vos gains dans votre bankroll pour la faire croître. Si vous commencez avec 1000€ et que vous atteignez 1500€, vous pouvez retirer 200€ tout en gardant 1300€ pour continuer. Votre bankroll a grandi, et vous avez profité de vos gains.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Cette approche vous permet de progresser graduellement. Au lieu de toujours repartir de zéro, vous construisez une base solide. Avec le temps, si vous êtes discipliné, votre bankroll peut devenir significative, ce qui vous permet de faire des paris plus importants tout en respectant les pourcentages.
              </p>

              <h2 className="text-3xl font-bold mt-12 mb-6 text-white">
                Éviter les Pièges Émotionnels
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Après une grosse perte, l'envie de "rattraper" est forte. Résistez. Augmenter vos mises après une perte est la meilleure façon de tout perdre rapidement. Après une grosse victoire, l'envie de "profiter" en misant plus est aussi forte. Résistez aussi. Restez discipliné, peu importe ce qui se passe.
              </p>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Fixez-vous des limites quotidiennes, hebdomadaires et mensuelles. Ne jamais dépasser ces limites, même si vous êtes en train de gagner. Le succès à long terme vient de la discipline, pas de la chance à court terme. Si vous avez atteint votre limite quotidienne, arrêtez pour aujourd'hui, même s'il y a d'autres opportunités.
              </p>

              <div className="bg-toxic-green/10 border border-toxic-green/30 rounded-xl p-6 mt-12">
                <h3 className="text-2xl font-bold mb-4 text-white flex items-center">
                  <Wallet className="w-6 h-6 text-toxic-green mr-3" />
                  Règles d'Or
                </h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Ne jamais miser plus que ce que vous pouvez vous permettre de perdre</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Utilisez des unités représentant 1% à 5% de votre bankroll</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Ne jamais dépasser 2 unités sur un seul pari</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Réduisez vos mises pendant les périodes difficiles, ne les augmentez pas</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-toxic-green mr-2">•</span>
                    <span>Fixez-vous des limites et respectez-les, peu importe les émotions</span>
                  </li>
                </ul>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GestionBankroll;

